import { useNavigate } from 'react-router-dom';

import AssigneeDropdown from './AssigneeDropdown.jsx';
import SentimentBadge from './SentimentBadge.jsx';
import SeverityBadge from './SeverityBadge.jsx';
import StatusBadge from './StatusBadge.jsx';

function formatReceived(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function truncate(s, n) {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}

export default function SubmissionRow({
  submission,
  isAdmin,
  onStatusChange,
  onAssigneeChange,
  selected = false,
  onToggleSelect,
  columnVisibility,
  isNew = false,
}) {
  const navigate = useNavigate();
  // Older rows predate problem_summary — fall back to intent.
  const displayText = submission.problem_summary || submission.intent || '';
  // Mirrors SubmissionTable: cells for hidden columns are not rendered.
  const show = (key) => columnVisibility?.[key] !== false;

  // A row is "new" when it's still unreviewed and unassigned (see
  // SubmissionTable). We highlight it hard until someone acts on it.
  const rowClass = [selected && 'selected', isNew && 'submission--new']
    .filter(Boolean)
    .join(' ');

  return (
    <tr className={rowClass || undefined}>
      <td className="col-select">
        <input
          type="checkbox"
          className="submission-checkbox"
          checked={selected}
          onChange={() => onToggleSelect?.(submission.id)}
          aria-label={`Select ${submission.full_name || 'submission'}`}
        />
      </td>
      <td>
        <button
          className="btn btn--secondary"
          onClick={() => navigate(`/submissions/${submission.id}`)}
        >
          View
        </button>
      </td>
      <td className="table__received">{formatReceived(submission.received_at)}</td>
      {show('who') && (
        <td>
          <div className="table__name">{submission.full_name || 'Unknown'}</div>
          <div className="table__email">{submission.email || ''}</div>
        </td>
      )}
      <td>
        <div className="table__intent" title={displayText}>
          {isNew && <span className="new-pill">NEW</span>}
          {truncate(displayText, 70)}
        </div>
      </td>
      {show('severity') && (
        <td>
          <SeverityBadge value={submission.severity} />
        </td>
      )}
      {show('sentiment') && (
        <td>
          <SentimentBadge value={submission.sentiment} />
        </td>
      )}
      {show('status') && (
        <td>
          <StatusBadge
            value={submission.status}
            editable={isAdmin}
            onChange={(next) => onStatusChange(submission.id, next)}
          />
        </td>
      )}
      {show('assigned_to_name') && (
        <td>
          <AssigneeDropdown
            value={submission.assigned_to_name}
            onChange={(next) => onAssigneeChange(submission.id, next)}
          />
        </td>
      )}
      {show('follow_up') && (
        <td>
          {submission.follow_up_requested ? (
            <span className="table__checkmark" aria-label="Follow-up requested">
              ✓
            </span>
          ) : (
            <span style={{ color: 'var(--color-text-muted)' }}>—</span>
          )}
        </td>
      )}
    </tr>
  );
}
